import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheck, FiClock, FiStar } from 'react-icons/fi';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { theme } from '../../styles/theme';

const RoadmapSection = styled.section`
  padding: ${theme.spacing['5xl']} 0;
  background: linear-gradient(135deg, ${theme.colors.neutral} 0%, rgba(165, 167, 221, 0.1) 100%);
  scroll-margin-top: 80px; /* Account for fixed navbar */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled(motion.h2)`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${theme.typography.sizes.xl};
  color: ${theme.colors.gray[600]};
  max-width: 700px;
  margin: 0 auto;
`;

const RoadmapTimeline = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing['4xl']};
  padding: ${theme.spacing['2xl']} 0;
  overflow-x: auto;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex-direction: column;
    align-items: center;
    overflow-x: visible;
  }
  
  /* Timeline line */
  &::before {
    content: '';
    position: absolute;
    top: 100px; /* Align with status icons */
    left: 80px;
    right: 80px;
    height: 4px;
    background: linear-gradient(90deg, 
      ${theme.colors.accent} 0%, 
      ${theme.colors.accent} 33%, 
      ${theme.colors.primary} 33%, 
      ${theme.colors.primary} 66%, 
      ${theme.colors.secondary} 66%, 
      ${theme.colors.secondary} 100%
    );
    z-index: 1;
    
    @media (max-width: ${theme.breakpoints.lg}) {
      display: none;
    }
  }
`;

const RoadmapCard = styled(Card)<{ status: string }>`
  position: relative;
  flex: 1;
  min-width: 320px;
  max-width: 380px;
  z-index: 2;
  background: ${theme.colors.white};
  border: 2px solid ${({ status }) => {
    switch (status) {
      case 'completed': return theme.colors.accent;
      case 'in-progress': return theme.colors.primary;
      case 'planned': return theme.colors.secondary;
      default: return theme.colors.gray[300];
    }
  }};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    max-width: 100%;
    min-width: 100%;
  }
  
  /* Timeline connector dot */
  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${({ status }) => {
      switch (status) {
        case 'completed': return theme.colors.accent;
        case 'in-progress': return theme.colors.primary;
        case 'planned': return theme.colors.secondary;
        default: return theme.colors.gray[300];
      }
    }};
    border: 4px solid ${theme.colors.white};
    box-shadow: ${theme.shadows.md};
    z-index: 3;
    
    @media (max-width: ${theme.breakpoints.lg}) {
      display: none;
    }
  }
`;

const RoadmapHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex-direction: row;
    text-align: left;
    gap: ${theme.spacing.md};
  }
`;

const StatusIcon = styled.div<{ status: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ status }) => {
    switch (status) {
      case 'completed': return theme.colors.accent;
      case 'in-progress': return theme.colors.primary;
      case 'planned': return theme.colors.secondary;
      default: return theme.colors.gray[300];
    }
  }};
  box-shadow: ${theme.shadows.lg};
  margin-bottom: ${theme.spacing.sm};
  
  svg {
    width: 30px;
    height: 30px;
    color: ${theme.colors.white};
  }
  
  @media (max-width: ${theme.breakpoints.lg}) {
    width: 50px;
    height: 50px;
    margin-bottom: 0;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.bold};
  text-transform: uppercase;
  letter-spacing: 1px;
  background: ${({ status }) => {
    switch (status) {
      case 'completed': return theme.colors.accent;
      case 'in-progress': return theme.colors.primary;
      case 'planned': return theme.colors.secondary;
      default: return theme.colors.gray[300];
    }
  }};
  color: ${theme.colors.white};
  white-space: nowrap;
`;

const RoadmapTitle = styled.h3`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.xl};
  
  @media (max-width: ${theme.breakpoints.lg}) {
    flex: 1;
    margin-bottom: ${theme.spacing.md};
  }
`;

const RoadmapDescription = styled.p`
  color: ${theme.colors.gray[600]};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.lg}) {
    text-align: left;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.gray[700]};
  font-size: ${theme.typography.sizes.sm};
  
  &:before {
    content: "•";
    color: ${theme.colors.primary};
    font-weight: bold;
    width: 8px;
  }
`;

interface RoadmapItem {
  status: 'completed' | 'in-progress' | 'planned';
  title: string;
  timeline: string;
  description: string;
  features: string[];
}

const roadmapItems: RoadmapItem[] = [
  {
    status: 'completed',
    title: 'Core Platform',
    timeline: 'July 2025',
    description: 'Foundation features for patient management and care coordination.',
    features: [
      'Patient dashboard management',
      'Basic routine tracking',
      'Medical history and analytics',
      'Upcoming activities and reminders',
      'Family and patient requesting system'
    ]
  },
  {
    status: 'in-progress',
    title: 'Advanced Analytics',
    timeline: 'Aug 2025',
    description: 'Early deployment and testing at local facilities.',
    features: [
      'Early stage testing at 2 aged care facilities, 1 NDIS organisation',
      'Gathering feedback from support workers', 
      'Consultation with care workers, families, and health professionals',
      'Ongoing research into AI-powered features',
    ]
  },
  {
    status: 'planned',
    title: 'AI-Powered Care',
    timeline: 'Q4 2025',
    description: 'LLM and Machine Learning technologies to gain predictive insights and improve care delivery.',
    features: [
      'Retrieval-Augmented Generation (RAG) to finetune AI models',
      'AI care recommendations',
      'Risk assessment algorithms', 
      'Predictive health monitoring'
    ]
  },
  
];

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed': return <Icon icon={FiCheck} />;
    case 'in-progress': return <Icon icon={FiClock} />;
    case 'planned': return <Icon icon={FiStar} />;
    default: return <Icon icon={FiClock} />;
  }
};

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 }
};

export const Roadmap: React.FC = () => {
  return (
    <RoadmapSection id="roadmap">
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Product Roadmap
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            We’re already making an impact. Care++ is live in two local care facilities and one NDIS organisation. 
            <br></br>
            <br></br>
            But we’re just getting started. We’re developing 
            AI-powered tools to analyse health trends and detect early risks before they arise.
          </SectionSubtitle>
        </SectionHeader>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <RoadmapTimeline>
            {roadmapItems.map((item, index) => (
              <motion.div key={index} variants={cardVariants}>
                <RoadmapCard status={item.status} hover>
                  <RoadmapHeader>
                    <StatusIcon status={item.status}>
                      {getStatusIcon(item.status)}
                    </StatusIcon>
                    <div>
                      <RoadmapTitle>{item.title}</RoadmapTitle>
                      <StatusBadge status={item.status}>{item.timeline}</StatusBadge>
                    </div>
                  </RoadmapHeader>
                  
                  <RoadmapDescription>{item.description}</RoadmapDescription>
                  
                  <FeatureList>
                    {item.features.map((feature, featureIndex) => (
                      <FeatureItem key={featureIndex}>{feature}</FeatureItem>
                    ))}
                  </FeatureList>
                </RoadmapCard>
              </motion.div>
            ))}
          </RoadmapTimeline>
        </motion.div>
      </Container>
    </RoadmapSection>
  );
};